import { useGoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";

import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { Button } from "@/components/ui";
import type { AuthMetadata } from "@/services/api-client/auth/types";
import { gtmService } from "@/services/gtm-service";
import { useAuthStore } from "@/store/auth.store";
import { cn } from "@/utils/cn";

interface GoogleAuthButtonProps {
  mode: "login" | "signup";
  onSuccess: () => void;
  metadata?: AuthMetadata;
  acceptMarketing?: boolean;
  quizId?: string;
  className?: string;
  isDisabled?: boolean;
}

export const GoogleAuthButton = ({
  mode,
  onSuccess,
  metadata,
  acceptMarketing = false,
  quizId,
  className,
  isDisabled,
}: GoogleAuthButtonProps) => {
  const { loginWithGoogle, isLoading } = useAuthStore();

  const triggerGoogleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        const user = await loginWithGoogle({ code, metadata, acceptMarketing });
        gtmService.trackLogInScs({ type: "google", email: user.email, quiz_id: quizId });
        onSuccess();
      } catch (err) {
        const message = err instanceof Error ? err.message : "unknown";
        gtmService.trackLogInFail({ type: "google", error: message, quiz_id: quizId });
        toast.error("Google sign-in failed");
      }
    },
    onError: ({ error }) => {
      gtmService.trackLogInFail({
        type: "google",
        error: error ?? "google_error",
        quiz_id: quizId,
      });
      toast.error("Google sign-in failed");
    },
    onNonOAuthError: () => {
      gtmService.trackLogInFail({
        type: "google",
        error: "google_non_oauth_error",
        quiz_id: quizId,
      });
    },
  });

  const handleClick = () => {
    gtmService.trackLogInTry({ type: "google", quiz_id: quizId });
    triggerGoogleLogin();
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={isLoading || isDisabled}
      className={cn("h-12 w-full gap-3 text-base text-black", className)}
    >
      <GoogleIcon className="size-5" />
      {mode === "signup" ? "Sign up with Google" : "Continue with Google"}
    </Button>
  );
};
